import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import api from '@/lib/api';

const RECORD_TYPES = ['Project', 'Proposal', 'Client', 'Property', 'DOB Filing', 'DOT Permit', 'FDNY Permit'];
const ENDPOINT = { Project: '/projects', Proposal: '/proposals', Client: '/clients', Property: '/properties', 'DOB Filing': '/dob-filings', 'DOT Permit': '/dot-permits', 'FDNY Permit': '/fdny-permits' };
const PERMIT_TYPES = ['DOB Filing', 'DOT Permit', 'FDNY Permit'];

function labelFor(type, r) {
  if (type === 'Project') return `${r.project_number || 'Project'}${r.name ? ` - ${r.name}` : ''}`;
  if (type === 'Proposal') return `${r.proposal_number || 'Draft'}${r.title ? ` - ${r.title}` : ''}`;
  if (type === 'Client') return r.name || 'Client';
  if (type === 'Property') return r.display_name || r.address || 'Property';
  if (type === 'DOB Filing') return `${r.job_number || 'Filing'}${r.filing_type ? ` · ${r.filing_type}` : ''}${r.description ? ` - ${r.description}` : ''}`;
  if (type === 'DOT Permit') return `${r.permit_code || 'Permit'}${r.permit_number ? ` · ${r.permit_number}` : ''}${r.description ? ` - ${r.description}` : ''}`;
  if (type === 'FDNY Permit') return `${r.permit_type || 'Permit'}${r.permit_number ? ` · ${r.permit_number}` : ''}`;
  return r.name || r.id;
}

// Section A: independent "Record Type" dropdown + a searchable record picker.
// onChange(recordType|null, recordId|null) is called on any change.
// For DOB Filing / DOT Permit the picker is scoped to `contextPropertyId` when provided.
export function RecordLinkFields({ recordType, recordId, onChange, testidPrefix = 'doc', contextPropertyId = null }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!recordType) { setRecords([]); return; }
    setLoading(true);
    let params = {};
    if (recordType === 'DOB Filing') params = contextPropertyId ? { property_id: contextPropertyId } : { all: true };
    else if (recordType === 'DOT Permit' || recordType === 'FDNY Permit') params = contextPropertyId ? { property_id: contextPropertyId } : {};
    api.get(ENDPOINT[recordType], { params })
      .then(r => setRecords(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [recordType, contextPropertyId]);

  const options = records.map(r => ({ value: r.id, label: labelFor(recordType, r) }));
  const isPermit = PERMIT_TYPES.includes(recordType);

  return (
    <>
      <div>
        <Label className="text-xs text-muted-foreground">Record Type</Label>
        <Select value={recordType || 'None'} onValueChange={v => onChange(v === 'None' ? null : v, null)}>
          <SelectTrigger className="bg-background border-border" data-testid={`${testidPrefix}-record-type`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="None">None</SelectItem>
            {RECORD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {recordType && (
        <div>
          <Label className="text-xs text-muted-foreground">{recordType}</Label>
          <div className="mt-1">
            <SearchableSelect
              options={options}
              value={recordId || ''}
              onChange={v => onChange(recordType, v || null)}
              placeholder={loading ? 'Loading...' : `Select ${recordType.toLowerCase()}`}
              searchPlaceholder={`Search ${recordType.toLowerCase()}s...`}
              emptyText={loading ? 'Loading...' : `No ${recordType.toLowerCase()}s found.`}
              testid={`${testidPrefix}-record-picker`}
            />
          </div>
          {isPermit && (
            <p className="text-xs text-muted-foreground mt-1" data-testid={`${testidPrefix}-scope-note`}>
              {contextPropertyId ? 'Scoped to this property\u2019s records.' : 'Showing all records across the portfolio.'}
            </p>
          )}
        </div>
      )}
    </>
  );
}

export default RecordLinkFields;
